/* eslint-disable max-lines */

import React, { useEffect, useState, useCallback } from 'react'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import DownloadIcon from '@mui/icons-material/DownloadOutlined'

import AutoComplete from 'components/AutoComplete'
import Spinner from 'components/Spinner'
import Table from 'components/Table'

import Api from 'services/api'
import blobToFileDownload from 'helpers/blobToFileDownload'
import { color } from 'styles/colors'

interface DealerOption { dealerId: number; dealerName: string }
interface ActivityReportEntry { deviceId: string; imei: string | null; iccid: string | null; dealerName: string; deprovisionReason: string | null; activityTime: string; activityType: 'deprovisioned' | 'reprovisioned' }
interface ActivityReportResponse { reportData: ActivityReportEntry[]; startDate: string; endDate: string; dealerId: number | null; totalCount: number }

const formatDate = (dateString: string) => (dateString ? new Date(dateString).toLocaleString() : '-')

const formatDateForInput = (date: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const HehmActivityReport = () => {
  const [dealers, setDealers] = useState<DealerOption[]>([])
  const [selectedDealer, setSelectedDealer] = useState<DealerOption | null>(null)
  const [reportData, setReportData] = useState<ActivityReportResponse | null>(null)
  const [isLoadingDealers, setIsLoadingDealers] = useState(true)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [dealerError, setDealerError] = useState(false)
  const [reportError, setReportError] = useState(false)

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [dealersData, lastRunData] = await Promise.all([
          Api.HomeHealth.getDealersWithHehmAccounts(),
          Api.HomeHealth.getLastActivityReportRun()
        ])
        setDealers(dealersData || [])
        setEndDate(formatDateForInput(new Date()))
        if (lastRunData?.lastRunAt) setStartDate(formatDateForInput(new Date(lastRunData.lastRunAt)))
      } catch (error) {
        console.error('Failed to fetch dealers:', error)
        setDealerError(true)
      } finally {
        setIsLoadingDealers(false)
      }
    }
    fetchInitialData()
  }, [])

  const runReport = useCallback(async () => {
    setIsLoadingReport(true)
    setReportError(false)
    try {
      const params: { dealerId?: number; startDate?: string; endDate?: string } = {}
      if (selectedDealer) params.dealerId = selectedDealer.dealerId
      if (startDate) params.startDate = startDate
      if (endDate) params.endDate = endDate
      setReportData(await Api.HomeHealth.getActivityReport(params))
    } catch (error) {
      console.error('Failed to fetch activity report:', error)
      setReportError(true)
    } finally {
      setIsLoadingReport(false)
    }
  }, [selectedDealer, startDate, endDate])

  const escapeCSV = (value: string | null) => {
    if (value == null) return ''
    const str = String(value)
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str
  }

  const downloadCSV = () => {
    if (!reportData?.reportData.length) return
    const headers = ['Device ID', 'IMEI', 'ICCID', 'Dealer Name', 'Activity Type', 'Deprovision Reason', 'Activity Time']
    const rows = reportData.reportData.map(e =>
      [e.deviceId, e.imei ?? null, e.iccid ?? null, e.dealerName, e.activityType, e.deprovisionReason, e.activityTime].map(escapeCSV).join(',')
    )
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const filename = selectedDealer
      ? `hehm_activity_report_${selectedDealer.dealerName.replace(/\s+/g, '_')}.csv`
      : 'hehm_activity_report_all_dealers.csv'
    blobToFileDownload(blob, filename)
  }

  const dealerOptions = dealers.map(d => ({ ...d, label: d.dealerName }))

  if (isLoadingDealers) return <Spinner variant="page" />
  if (dealerError) return <S.ErrorMessage>Failed to load dealers. Please refresh and try again.</S.ErrorMessage>

  return (
    <S.Container>
      <S.FilterSection>
        <S.FilterRow>
          <S.FilterItem>
            <S.Label>Dealer</S.Label>
            <AutoComplete
              label=""
              options={[
                { dealerId: null, dealerName: 'All Dealers with HEHM Accounts', label: 'All Dealers with HEHM Accounts' },
                ...dealerOptions
              ]}
              onSelected={(dealer) => setSelectedDealer(dealer.dealerId !== null ? dealer : null)}
              initialValue={selectedDealer?.dealerName || 'All Dealers with HEHM Accounts'}
              getOptionKey={(option) => option.dealerId ?? 'all'}
              getOptionLabel={(option) => option.label || option.dealerName}
            />
          </S.FilterItem>
          <S.FilterItem>
            <S.Label>Start Date</S.Label>
            <S.DateInput type="datetime-local" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </S.FilterItem>
          <S.FilterItem>
            <S.Label>End Date</S.Label>
            <S.DateInput type="datetime-local" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </S.FilterItem>
        </S.FilterRow>
        <S.ButtonRow>
          <Button decoration="primary" text="Run Report" onPress={runReport} showSpinner={isLoadingReport} />
        </S.ButtonRow>
      </S.FilterSection>

      {reportError && <S.ErrorMessage>Failed to load report. Please try again.</S.ErrorMessage>}

      {reportData && (
        <S.ReportSection>
          <S.ReportHeader>
            <S.ReportMeta>
              <span>Report Period: {formatDate(reportData.startDate)} - {formatDate(reportData.endDate)}</span>
              <span>Total Records: {reportData.totalCount}</span>
            </S.ReportMeta>
            {reportData.reportData.length > 0 && (
              <S.DownloadButton>
                <DownloadIcon style={{ color: color('primaryButton') }} />
                <Button decoration="primary" text="Download CSV" onPress={downloadCSV} />
              </S.DownloadButton>
            )}
          </S.ReportHeader>

          {reportData.reportData.length > 0 ? (
            <Table items={reportData.reportData}>
              <Table.Header>
                <Table.HeaderCell title="Device ID" />
                <Table.HeaderCell title="IMEI" />
                <Table.HeaderCell title="ICCID" />
                <Table.HeaderCell title="Dealer Name" />
                <Table.HeaderCell title="Activity Type" />
                <Table.HeaderCell title="Deprovision Reason" />
                <Table.HeaderCell title="Activity Time" />
              </Table.Header>
              {reportData.reportData.map((row) => (
                <Table.Row key={`${row.deviceId}-${row.activityType}-${row.activityTime}`}>
                  <S.Cell>{row.deviceId}</S.Cell>
                  <S.Cell>{row.imei || '-'}</S.Cell>
                  <S.Cell>{row.iccid || '-'}</S.Cell>
                  <S.CellBreak>{row.dealerName}</S.CellBreak>
                  <S.CellBreak>{row.activityType}</S.CellBreak>
                  <S.CellBreak>{row.deprovisionReason || '-'}</S.CellBreak>
                  <S.CellBreak>{formatDate(row.activityTime)}</S.CellBreak>
                </Table.Row>
              ))}
            </Table>
          ) : (
            <S.NoData>No activity found for the selected period.</S.NoData>
          )}
        </S.ReportSection>
      )}
    </S.Container>
  )
}

export default HehmActivityReport

const S = {
  Container: styled.div`
    padding: 20px 0;
  `,
  FilterSection: styled.div`
    margin-bottom: 20px;
    padding: 15px;
    background: ${color('paneBackground')};
    border-radius: 4px;
  `,
  FilterRow: styled.div`
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
    margin-bottom: 15px;
  `,
  FilterItem: styled.div`
    flex: 1;
    min-width: 200px;
  `,
  Label: styled.label`
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    color: ${color('headerText')};
  `,
  DateInput: styled.input`
    width: 100%;
    padding: 8px;
    border: 1px solid ${color('border')};
    border-radius: 4px;
    font-size: 14px;
  `,
  ButtonRow: styled.div`
    display: flex;
    justify-content: flex-end;
    * { text-transform: none !important; }
    & button { border: none !important; }
  `,
  ReportSection: styled.div`
    margin-top: 20px;
  `,
  ReportHeader: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    overflow: visible;
  `,
  ReportMeta: styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    color: ${color('headerText')};
  `,
  DownloadButton: styled.div`
    display: flex;
    align-items: center;
    padding: 4px;
    overflow: visible;
    & * { text-transform: none !important; }
    & button { border: none !important; }
  `,
  NoData: styled.div`
    text-align: center;
    padding: 40px;
    color: ${color('secondaryText')};
  `,
  ErrorMessage: styled.div`
    padding: 12px 16px;
    margin: 10px 0;
    background: ${color('paneBackground')};
    color: ${color('errorText')};
    border-radius: 4px;
  `,
  Cell: styled(Table.Cell)`
    overflow-wrap: anywhere;
  `,
  CellBreak: styled(Table.Cell)`
    overflow-wrap: break-word;
  `
}
